import { useEffect, useState } from "react";
import { useRemote } from "../Utils/Utils";
import "./ReviewVideoPreview.scss";

// StandardResponse unwrap — bunny endpoints return { data: <value> }; some return the bare value.
const unwrap = (r) => (r && typeof r === 'object' && 'data' in r ? r.data : r);

/**
 * Read-only video preview for the review page.
 *
 * By default it resolves the LIVE videoId for a ticker+template via /server-preview (never
 * snapshotted, so a replaced video can't go stale) and renders the Bunny player. When
 * `snapshotVideoData` is supplied (a cancelled/rejected historical view), it instead uses the PINNED
 * videoId + transcript captured at rejection time, so the reviewer sees exactly what was rejected.
 * Either way the transcript is the author's finalised text from our DB (videoTranscription) — NOT
 * Bunny's auto-captions.
 */
const ReviewVideoPreview = ({ ticker, template, snapshotVideoData }) => {
	const remote = useRemote();
	const [playUrl, setPlayUrl] = useState(null);
	const [transcript, setTranscript] = useState('');
	const [state, setState] = useState('loading'); // 'loading' | 'ready' | 'error'

	const snapshotVideoId = snapshotVideoData?.videoId || null;
	const snapshotTranscript = snapshotVideoData?.videoTranscription || '';

	useEffect(() => {
		let cancelled = false;
		setState('loading');
		setPlayUrl(null);
		setTranscript('');

		// Assemble the play URL from a pinned guid (snapshot) or resolve the live guid via /server-preview.
		const resolveGuidAndTranscript = snapshotVideoId
			? Promise.resolve({ guid: snapshotVideoId, dbTranscript: snapshotTranscript })
			: remote.post('/server-preview', { template, tickerGroup: ticker }).then(resp => ({
				guid: resp && resp.videoId,
				dbTranscript: (resp && resp.videoTranscription) || '',
			}));

		resolveGuidAndTranscript.then(({ guid, dbTranscript }) => {
			if (!guid) { if (!cancelled) setState('error'); return; }
			return remote.get(`bunny/videos/${guid}/play-url`).then(playRes => {
				if (cancelled) return;
				const url = unwrap(playRes);
				if (!url) { setState('error'); return; }
				setPlayUrl(url);
				setTranscript(dbTranscript);
				setState('ready');
			});
		}).catch(() => { if (!cancelled) setState('error'); });
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticker, template, snapshotVideoId, snapshotTranscript]);

	if (state === 'error') return <div className="rvp-msg">This content is no longer available.</div>;
	if (state === 'loading') return <div className="rvp-msg">Loading video…</div>;
	return (
		<div className="rvp-wrap">
			<iframe
				title="Video preview"
				src={playUrl}
				loading="lazy"
				className="rvp-iframe"
				allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
				allowFullScreen
			/>
			{transcript && (
				<details className="rvp-transcript" open>
					<summary>Transcript</summary>
					<pre>{transcript}</pre>
				</details>
			)}
		</div>
	);
};

export default ReviewVideoPreview;
